import type { AuthProvider } from '@refinedev/core';
import { ApiError, api, clearToken, getToken, refreshAccessToken, setToken } from './api-client';

interface LoginResponse {
  token: string;
  expires_in: number;
  admin: {
    id: string;
    email: string;
    name: string | null;
    roles: string[];
    permissions: string[];
  };
}

interface JwtPayload {
  sub?: string;
  id?: string;
  email: string;
  name?: string | null;
  roles?: string[];
  permissions?: string[];
  exp?: number;
}

function decodeJwt(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const [, payloadPart] = parts;
    if (!payloadPart) return null;
    const payload = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    return JSON.parse(atob(padded)) as JwtPayload;
  } catch {
    return null;
  }
}

export const authProvider: AuthProvider = {
  login: async ({ email, password }: { email: string; password: string }) => {
    try {
      const res = await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password },
      });
      setToken(res.token);
      return { success: true, redirectTo: '/' };
    } catch (err) {
      if (err instanceof ApiError) {
        const reasonMap: Record<string, string> = {
          rate_limited: 'Muitas tentativas. Aguarde 15 min.',
          invalid_credentials: 'Email ou senha inválidos.',
          account_disabled: 'Conta desativada.',
          account_locked: 'Conta bloqueada temporariamente.',
        };
        return {
          success: false,
          error: {
            name: err.errorCode,
            message: reasonMap[err.errorCode] ?? 'Erro ao fazer login.',
          },
        };
      }
      return {
        success: false,
        error: { name: 'login_error', message: 'Erro de conexão.' },
      };
    }
  },

  logout: async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // cookie fica pro servidor expirar; logout local segue mesmo assim
    }
    clearToken();
    return { success: true, redirectTo: '/login' };
  },

  check: async () => {
    const token = getToken();
    const payload = token ? decodeJwt(token) : null;
    const expired = !payload || (payload.exp !== undefined && payload.exp * 1000 < Date.now());

    if (expired) {
      // Sem access válido — tenta renovar pelo cookie HttpOnly antes de
      // mandar pro login. É isso que segura a sessão por 7 dias.
      const refreshed = await refreshAccessToken();
      if (refreshed) return { authenticated: true };
      clearToken();
      return {
        authenticated: false,
        redirectTo: '/login',
        logout: true,
        error: { name: 'expired', message: 'Sessão expirada.' },
      };
    }

    // Renovação proativa quando falta <15min: evita o 401 no meio de uma ação.
    if (payload.exp !== undefined && payload.exp * 1000 - Date.now() < 15 * 60 * 1000) {
      void refreshAccessToken();
    }
    return { authenticated: true };
  },

  getPermissions: async () => {
    const token = getToken();
    if (!token) return null;
    return decodeJwt(token)?.permissions ?? [];
  },

  getIdentity: async () => {
    const token = getToken();
    if (!token) return null;
    const p = decodeJwt(token);
    if (!p) return null;
    return {
      id: p.sub ?? p.id ?? '',
      email: p.email,
      name: p.name ?? p.email,
      roles: p.roles ?? [],
      permissions: p.permissions ?? [],
    };
  },

  onError: async (error) => {
    if (error instanceof ApiError && error.status === 401) {
      clearToken();
      return { logout: true, redirectTo: '/login' };
    }
    return {};
  },
};

export function hasPermission(permissions: string[] | null | undefined, slug: string): boolean {
  if (!permissions) return false;
  if (permissions.includes('*')) return true;
  return permissions.includes(slug);
}
